import { motion } from 'framer-motion';
import {
    ArrowRight,
    Award,
    Shield,
    Zap
} from 'lucide-react';
import { Link } from 'react-router-dom';

const CredentialMonetization = () => {
    return (
        <div className="min-h-screen pt-20">
            {/* Hero Section */}
            <section className="py-20 bg-surface-100">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                            Privacy-Preserving Credential Monetization
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Create new revenue streams by setting prices for credential verification while maintaining complete privacy and user control over their data.
                        </p>
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <h2 className="text-2xl font-bold text-white mb-6">
                                Monetize Your Reusable ID Credentials
                            </h2>
                            <div className="space-y-6">
                                <div className="glass rounded-xl p-6 border border-white/20">
                                    <div className="flex items-start space-x-4">
                                        <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                            <Zap className="w-5 h-5 text-green-400" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-white mb-2">Revenue per Verification</h3>
                                            <p className="text-gray-300 text-sm">Set custom pricing for each credential verification. Earn revenue every time someone verifies your issued credentials.</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="glass rounded-xl p-6 border border-white/20">
                                    <div className="flex items-start space-x-4">
                                        <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                            <Shield className="w-5 h-5 text-blue-400" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-white mb-2">Privacy-First Architecture</h3>
                                            <p className="text-gray-300 text-sm">Zero-knowledge proofs ensure verification happens without exposing sensitive personal information.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="glass rounded-xl p-6 border border-white/20">
                                    <div className="flex items-start space-x-4">
                                        <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                            <Award className="w-5 h-5 text-purple-400" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-white mb-2">Premium Credential Tiers</h3>
                                            <p className="text-gray-300 text-sm">Create different pricing tiers for basic verification vs. detailed credential information sharing.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <div className="glass rounded-2xl p-8 border border-white/20">
                                <h3 className="text-lg font-bold text-white mb-6 text-center">Monetization Dashboard</h3>
                                <div className="space-y-4">
                                    <div className="bg-gradient-to-r from-green-500/20 to-blue-500/20 rounded-xl p-4 border border-white/20">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-white font-medium">University Degree</span>
                                            <span className="text-green-400 font-bold">$0.50</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-400">1,247 verifications</span>
                                            <span className="text-green-400">+$623.50</span>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-4 border border-white/20">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-white font-medium">Professional License</span>
                                            <span className="text-purple-400 font-bold">$1.25</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-400">892 verifications</span>
                                            <span className="text-purple-400">+$1,115.00</span>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-xl p-4 border border-white/20">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-white font-medium">Employment Certificate</span>
                                            <span className="text-orange-400 font-bold">$0.25</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-400">2,156 verifications</span>
                                            <span className="text-orange-400">+$539.00</span>
                                        </div>
                                    </div>
                                    
                                    <div className="border-t border-white/20 pt-4">
                                        <div className="flex justify-between items-center">
                                            <span className="text-white font-bold">Total Revenue</span>
                                            <span className="text-neon-300 font-bold text-xl">$2,277.50</span>
                                        </div>
                                        <div className="text-gray-400 text-sm text-center mt-2">This month</div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-20 bg-gradient-to-r from-primary-600 to-neon-600">
                <div className="container-custom text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                            Start Monetizing Your Credentials Today
                        </h2>
                        <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                            Turn your verifiable credentials into a revenue stream while maintaining user privacy.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href="https://app.passhub.documint.id/signup" target="_blank" rel="noopener noreferrer" className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center">
                                Start Free Trial
                                <ArrowRight size={20} className="ml-2" />
                            </a>
                            <Link to="/contact" className="border border-white/20 text-white hover:bg-white/10 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center">
                                Contact Sales
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default CredentialMonetization;